import React, { useEffect, useRef, useState } from 'react';
import { Music, Upload, X, AlertCircle, Loader2, Play, Pause, Image as ImageIcon, Film, ChevronDown, Eye, EyeOff, LayoutGrid, Pencil } from 'lucide-react';
import { NodeData, type ReferenceTag } from '../../types';
import { REFERENCE_TAGS, referenceTagInfo } from '../../utils/referenceTags';
import { useFileUrl } from '../../hooks/useFileUrl';
import { FileImage } from '../FileImage';
import { audioExtensionFromMime, formatBytes, formatDuration } from '../../utils/audioReference';
import { loadWaveform } from '../../utils/audioWaveform';
import { AudioWaveform } from './AudioWaveform';
import { REFERENCE_ACCEPT, imageExtensionFromMime, inferReferenceKind, referenceStorageName, validateReferenceFile, videoExtensionFromMime, type ReferenceKind } from '../../utils/referenceMedia';
import { probeVideoDuration } from '../../utils/videoTrim';
import type { ReferenceChoice } from '../../utils/workspaceEdit';
import { VideoPlayer } from '../VideoPlayer';
import { ReferencePicker } from './ReferencePicker';
import { SettingChip, type ChipOption } from './composer/Composer';
import type { ReferenceSharing } from './AddReferencesButton';
import { useVideoPosters } from './useVideoPosters';

type SaveCategory = 'Generated_Images' | 'Uploaded_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads';

/** A folded Reference node: the title bar and one row with a small picture. */
export const REFERENCE_COMPACT_HEIGHT = 104;
const DEFAULT_OPEN_HEIGHT = 300;
// The least a node with its preview open is given. It holds, with nothing cut off or scrolling: the
// card's header and border (42px), the file's row (50px), the preview at its smallest (92px, see the
// three views) and the 12px around and between them.
const MIN_OPEN_HEIGHT = 220;

interface ReferenceNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  userId?: string;
  workspaceName?: string;
  nodeId?: string;
  onAutoSaveFile?: (fileData: string | Blob, fileName: string, category?: SaveCategory, fileExtension?: string) => Promise<void>;
  isViewOnly?: boolean;
  ownerUserId?: string;
  /** The workspace's media, read when the picker opens (in a project, with the other workspaces' Reference nodes). */
  getChoices?: () => ReferenceChoice[];
  /** In a project: its other workspaces' Reference nodes are in the list as well. */
  sharing?: ReferenceSharing;
  /** The node's size, and a way to change it, for folding the preview away. */
  nodeSize?: { width: number; height: number };
  onResize?: (size: { width: number; height: number }) => void;
}

/** A data URL's bytes as a file, without fetching (the app's security policy does not allow fetching data URLs). */
const dataUrlToFile = (dataUrl: string, name: string): File => {
  const comma = dataUrl.indexOf(',');
  const head = dataUrl.slice(0, comma);
  const body = dataUrl.slice(comma + 1);
  const type = /^data:([^;,]+)/i.exec(head)?.[1] || 'application/octet-stream';
  const bytes = /;base64/i.test(head)
    ? Uint8Array.from(atob(body), char => char.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(body));
  return new File([bytes], name, { type });
};

/** A file name for media that so far only had a label. */
const fileNameFor = (choice: ReferenceChoice, mimeType: string): string => {
  const stem = choice.label.replace(/\.[a-z0-9]{2,4}$/i, '');
  const base = stem.replace(/[^\w\-. ]+/g, '').trim().slice(0, 60) || 'reference';
  const ext = choice.kind === 'audio' ? audioExtensionFromMime(mimeType, '')
    : choice.kind === 'video' ? videoExtensionFromMime(mimeType, '')
    : imageExtensionFromMime(mimeType, '');
  return `${base}.${ext}`;
};

/** Measure an audio clip's length without adding it to the page. */
const probeAudioDuration = (file: File): Promise<number | undefined> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const done = (value: number | undefined) => { URL.revokeObjectURL(url); resolve(value); };
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? audio.duration : undefined);
    audio.onerror = () => done(undefined);
    audio.src = url;
  });

const probeImageSize = (dataUrl: string): Promise<{ width: number; height: number } | undefined> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(undefined);
    img.src = dataUrl;
  });

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatLabel = (kind: ReferenceKind, mimeType?: string, fileName?: string): string => {
  if (kind === 'audio') return audioExtensionFromMime(mimeType || '', fileName || '').toUpperCase();
  if (kind === 'video') return videoExtensionFromMime(mimeType || '', fileName || '').toUpperCase();
  return imageExtensionFromMime(mimeType || '', fileName || '').toUpperCase();
};

/** Fields of every kind, blank, so switching kinds leaves nothing behind. */
const CLEARED: Partial<NodeData> = {
  referenceKind: undefined, localCopyName: undefined,
  imageUrl: undefined, originalWidth: undefined, originalHeight: undefined,
  audioUrl: undefined, audioFileName: undefined, audioMimeType: undefined, audioSizeBytes: undefined, audioDurationSec: undefined,
  videoUri: undefined, videoFileName: undefined, videoMimeType: undefined, videoSizeBytes: undefined, videoDurationSec: undefined,
};

const KIND_LABEL: Record<ReferenceKind, string> = { image: 'Image', audio: 'Audio', video: 'Video' };
/** What the node holds, as it is called in a sentence: "Remove the audio file from this node". */
const KIND_NOUN: Record<ReferenceKind, string> = { image: 'image', audio: 'audio file', video: 'video' };
const KIND_HINT: Record<ReferenceKind, string> = {
  image: 'Use it in an Image or Video node: connect it, or choose it there under Add reference.',
  audio: 'Use it in a Video node in Reference mode (connect it, or choose it under Add reference) as background music or a voice / sound reference.',
  video: 'Use it in a Video node in Reference mode (connect it, or choose it under Add reference) to continue or restyle this clip; trim the part to use there.',
};

/** A file's name without its extension, as a reference's first name. */
const stripExtension = (name: string): string => name.replace(/\.[a-z0-9]{2,4}$/i, '') || name;

/**
 * The audio view: waveform, transport and metadata for a clip held by the node.
 */
const AudioView: React.FC<{
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  userId?: string;
  workspaceName?: string;
  ownerUserId?: string;
}> = ({ data, onDataChange, userId, workspaceName, ownerUserId }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { url: resolvedUrl, isLoading, error: resolveError } = useFileUrl(userId, data.audioUrl, workspaceName, ownerUserId);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState<number | undefined>(data.audioDurationSec);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [waveformFailed, setWaveformFailed] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentSec(0);
    setDurationSec(data.audioDurationSec);
  }, [data.audioUrl, data.audioDurationSec]);

  useEffect(() => {
    setPeaks(null);
    setWaveformFailed(false);
    if (!resolvedUrl) return;
    let cancelled = false;
    loadWaveform(resolvedUrl)
      .then((result) => { if (!cancelled) setPeaks(result); })
      .catch(() => { if (!cancelled) setWaveformFailed(true); });
    return () => { cancelled = true; };
  }, [resolvedUrl]);

  useEffect(() => {
    if (!isPlaying) return;
    let frame = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio) setCurrentSec(audio.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !resolvedUrl) return;
    if (audio.paused) void audio.play().catch(() => undefined);
    else audio.pause();
  };

  const seekTo = (fraction: number) => {
    const audio = audioRef.current;
    const total = audio?.duration;
    if (!audio || !total || !Number.isFinite(total)) return;
    audio.currentTime = fraction * total;
    setCurrentSec(audio.currentTime);
  };

  const progress = durationSec && durationSec > 0 ? Math.min(1, currentSec / durationSec) : 0;
  const waveformState = peaks ? 'ready' : waveformFailed ? 'failed' : 'loading';

  return (
    <>
      <div className="flex-1 min-h-[92px] rounded-xl bg-ink p-3 flex flex-col gap-2" data-waveform={!isLoading && resolvedUrl ? waveformState : 'idle'}>
        <div className="flex-1 min-h-[28px]">
          {isLoading ? (
            <div className="h-full flex items-center justify-center gap-2 text-xs text-ink-faint"><Loader2 size={14} className="animate-spin" /> Loading audio</div>
          ) : resolvedUrl ? (
            <AudioWaveform peaks={peaks} failed={waveformFailed} progress={progress} durationSec={durationSec} currentSec={currentSec} onSeek={seekTo} onTogglePlay={togglePlay} />
          ) : (
            <div className="h-full flex items-center justify-center gap-2 text-xs text-lemon"><AlertCircle size={14} /> {resolveError || 'Audio unavailable'}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            disabled={!resolvedUrl || isLoading}
            className="no-drag ns-icon-btn ns-icon-btn-tone focus-visible:outline-lavender"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause /> : <Play className="ml-0.5" />}
          </button>
          <span className="text-2xs font-medium text-cream tabular-nums">
            {formatDuration(currentSec) || '0s'}
            <span className="text-ink-faint"> / {formatDuration(durationSec) || '--'}</span>
          </span>
          <span className="ns-t-label ml-auto truncate">
            {isLoading ? '' : waveformFailed ? 'Waveform unavailable' : peaks ? (formatLabel('audio', data.audioMimeType, data.audioFileName) || 'Audio') : 'Reading waveform'}
          </span>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={resolvedUrl ?? undefined}
        preload="metadata"
        className="hidden"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (!Number.isFinite(d)) return;
          setDurationSec(d);
          if (data.audioDurationSec === undefined) onDataChange({ ...data, audioDurationSec: d });
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); setCurrentSec(0); }}
      />
    </>
  );
};

/** The video view: a player on a dark panel. */
const VideoView: React.FC<{
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  userId?: string;
  workspaceName?: string;
  ownerUserId?: string;
}> = ({ data, onDataChange, userId, workspaceName, ownerUserId }) => {
  const { url, isLoading, error } = useFileUrl(userId, data.videoUri, workspaceName, ownerUserId);
  return (
    <div className="flex-1 min-h-[92px] rounded-xl bg-ink overflow-hidden grid place-items-center" data-video={isLoading ? 'loading' : url ? 'ready' : 'failed'}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-ink-faint"><Loader2 size={14} className="animate-spin" /> Loading video</div>
      ) : url ? (
        <VideoPlayer
          src={url}
          className="no-drag w-full h-full"
          label="Reference video"
          // A clip picked from the workspace may come without its length
          onDuration={(seconds) => { if (data.videoDurationSec === undefined) onDataChange({ ...data, videoDurationSec: seconds }); }}
        />
      ) : (
        <div className="flex items-center gap-2 text-xs text-lemon"><AlertCircle size={14} /> {error || 'Video unavailable'}</div>
      )}
    </div>
  );
};

/** The image view: the picture on a dark panel. */
const ImageView: React.FC<{ data: NodeData; userId?: string; workspaceName?: string }> = ({ data, userId, workspaceName }) => (
  <div className="flex-1 min-h-[92px] rounded-xl bg-ink overflow-hidden grid place-items-center">
    <FileImage
      src={data.imageUrl || ''}
      userId={userId}
      workspaceName={workspaceName}
      alt={data.title || 'Reference image'}
      className="w-full h-full object-contain"
      fallback={<div className="flex items-center gap-2 text-xs text-lemon"><AlertCircle size={14} /> Image unavailable</div>}
    />
  </div>
);

/**
 * The reference's name, typed in the file's row while it is being renamed (the name itself is
 * the node's title, in the header): Enter or leaving the field keeps it, Esc goes back.
 * `onClose` says whether a key closed it, so the focus can go back to the button that opened it.
 */
const NameField: React.FC<{ value: string; onCommit: (name: string) => void; onClose: (byKey: boolean) => void }> = ({ value, onCommit, onClose }) => {
  const [draft, setDraft] = useState(value);
  const fieldRef = useRef<HTMLInputElement>(null);
  const byKey = useRef(false);
  const cancelled = useRef(false);
  useEffect(() => {
    fieldRef.current?.focus({ preventScroll: true });
    fieldRef.current?.select();
  }, []);
  return (
    <input
      ref={fieldRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const name = draft.trim();
        if (!cancelled.current && name && name !== value) onCommit(name);
        onClose(byKey.current);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        e.stopPropagation(); // the field's keys, not the canvas's (there Esc lets go of the selected nodes)
        byKey.current = true;
        cancelled.current = e.key === 'Escape';
        e.currentTarget.blur();
      }}
      maxLength={80}
      spellCheck={false}
      aria-label="Reference name"
      title="Enter keeps the name, Esc goes back"
      className="ns-field ns-field-sm no-drag flex-1 min-w-0"
      data-reference-name
    />
  );
};

/** The tags to choose from, each with a dot of its colour, and "No tag" first (with a clear dot, so the words line up). */
const TAG_OPTIONS: ChipOption<ReferenceTag | ''>[] = [
  { value: '', label: 'No tag', icon: <span className="ns-tone-dot block bg-transparent" /> },
  ...REFERENCE_TAGS.map(option => ({ value: option.id, label: option.label, icon: <span className="ns-tone-dot block" data-tone={option.tone} /> })),
];

/**
 * The reference's tag: a soft chip in the colour of the kind it names (a Character tag is the
 * Character node's colour), which opens the list of tags as every setting chip in a node does.
 */
const TagSelect: React.FC<{ tag?: ReferenceTag; editable: boolean; onChange: (tag: ReferenceTag | undefined) => void }> = ({ tag, editable, onChange }) => {
  const info = referenceTagInfo(tag);
  if (!editable) return info ? <span className="ns-chip ns-chip-sm ns-chip-tone shrink-0" data-tone={info.tone}>{info.label}</span> : null;
  return (
    <span className="inline-flex min-w-0" data-reference-tag={tag ?? ''}>
      <SettingChip<ReferenceTag | ''>
        label="Tag"
        value={tag ?? ''}
        options={TAG_OPTIONS}
        onChange={(next) => onChange(next || undefined)}
        size="sm"
        tone={info?.tone}
        shrink
      />
    </span>
  );
};

/** The small picture of a folded node. */
const SmallPicture: React.FC<{ kind: ReferenceKind; data: NodeData; nodeId?: string; userId?: string; ownerUserId?: string; workspaceName?: string; title?: string }> = ({
  kind, data, nodeId, userId, ownerUserId, workspaceName, title,
}) => {
  const box = 'w-10 h-10 rounded-lg overflow-hidden shrink-0 grid place-items-center';
  if (kind === 'image') {
    return (
      <span className={`${box} bg-ink`} title={title}>
        <FileImage src={data.imageUrl || ''} userId={userId} workspaceName={workspaceName} alt="" className="w-full h-full object-cover" fallback={<ImageIcon size={16} className="text-cream/50" />} />
      </span>
    );
  }
  if (kind === 'video') return <SmallPoster data={data} nodeId={nodeId} userId={userId} ownerUserId={ownerUserId} workspaceName={workspaceName} box={box} title={title} />;
  // Sound has no picture: the round mark of its kind stands in for one
  return <span className="ns-mark" title={title}><Music /></span>;
};

const SmallPoster: React.FC<{ data: NodeData; nodeId?: string; userId?: string; ownerUserId?: string; workspaceName?: string; box: string; title?: string }> = ({
  data, nodeId, userId, ownerUserId, workspaceName, box, title,
}) => {
  const video = { id: nodeId || 'reference', url: data.videoUri || '', label: data.videoFileName };
  const { posterOf } = useVideoPosters(data.videoUri ? [video] : [], { userId, ownerUserId, workspaceName });
  const poster = data.videoUri ? posterOf(video) : undefined;
  return (
    <span className={`${box} bg-ink`} title={title}>
      {poster ? <img src={poster} alt="" draggable={false} className="w-full h-full object-cover" data-poster /> : <Film size={16} className="text-cream/50" />}
    </span>
  );
};

/**
 * One reference of any kind: an image, an audio file or a video. The node adapts
 * its view to what it holds and feeds connected nodes accordingly (images to
 * image and video generators, audio and clips to Seedance reference mode).
 * Its media comes from the workspace (a picker with pictures) or from the computer,
 * and its preview can be folded away to leave one row.
 */
export const ReferenceNode: React.FC<ReferenceNodeProps> = ({
  data, onDataChange, userId, workspaceName, nodeId, onAutoSaveFile, isViewOnly = false, ownerUserId, getChoices, nodeSize, onResize, sharing,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const renameRef = useRef<HTMLButtonElement>(null);
  // The row shows the name field in place of the tag and details while the name is being typed
  const [renaming, setRenaming] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The picker is open while it has a button to hang from
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  // Read at every render while the list is open, so the project's other references appear as they are read
  const choices: ReferenceChoice[] = pickerAnchor && getChoices ? getChoices() : [];
  const kind = inferReferenceKind(data);
  const compact = !!kind && !!data.referenceCompact;

  useEffect(() => { setError(null); }, [data.imageUrl, data.audioUrl, data.videoUri]);
  // The row can go while the name is being typed (the file removed from elsewhere, the workspace made view only)
  useEffect(() => { if (!kind || isViewOnly) setRenaming(false); }, [kind, isViewOnly]);

  /** Takes a file in; `extra` is merged into the node data (a tag carried over from a picked reference). */
  const processFile = async (file: File, extra: Partial<NodeData> = {}) => {
    if (isViewOnly) return;
    const check = validateReferenceFile(file);
    if (check.problem) { setError(check.problem); return; }
    const nextKind = check.kind as ReferenceKind;
    setIsBusy(true);
    setError(null);
    try {
      const dataUrl = await readAsDataUrl(file);
      // The local copy gets the name the workspace save will upload it under, so the app finds it on disk
      // instead of downloading the cloud copy (see referenceStorageName and prepareDataForStorage).
      const stamp = Date.now() + Math.floor(Math.random() * 1000);
      const saveLocally = (name: ReturnType<typeof referenceStorageName>) => {
        onAutoSaveFile?.(dataUrl, name.fileName, name.category, name.extension).catch(() => undefined);
      };
      // A name the user gave stays when the media changes; otherwise the file's name is used
      const base = { ...data, ...CLEARED, referenceKind: nextKind, title: data.referenceNamed && data.title ? data.title : stripExtension(file.name), ...extra, timestamp: Date.now() };
      if (nextKind === 'image') {
        const size = await probeImageSize(dataUrl);
        const name = referenceStorageName('image', stamp, file.type, file.name);
        onDataChange({ ...base, imageUrl: dataUrl, originalWidth: size?.width, originalHeight: size?.height, localCopyName: name.fileName });
        saveLocally(name);
      } else if (nextKind === 'audio') {
        const durationSec = await probeAudioDuration(file);
        const mimeType = file.type || (audioExtensionFromMime('', file.name) === 'wav' ? 'audio/wav' : 'audio/mpeg');
        const name = referenceStorageName('audio', stamp, mimeType, file.name);
        onDataChange({ ...base, audioUrl: dataUrl, audioFileName: file.name, audioMimeType: mimeType, audioSizeBytes: file.size, audioDurationSec: durationSec, localCopyName: name.fileName });
        saveLocally(name);
      } else {
        const durationSec = await probeVideoDuration(file).catch(() => 0);
        const mimeType = file.type || `video/${videoExtensionFromMime('', file.name) === 'mov' ? 'quicktime' : videoExtensionFromMime('', file.name)}`;
        const name = referenceStorageName('video', stamp, mimeType, file.name);
        onDataChange({ ...base, videoUri: dataUrl, videoFileName: file.name, videoMimeType: mimeType, videoSizeBytes: file.size, videoDurationSec: durationSec || undefined, localCopyName: name.fileName });
        saveLocally(name);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read the file.');
    } finally {
      setIsBusy(false);
    }
  };

  const onFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) void processFile(file);
  };

  /** Fold the preview away to one row, or open it again at the height it had. */
  const setCompact = (next: boolean) => {
    if (isViewOnly || next === compact) return;
    const width = nodeSize?.width ?? 320;
    if (next) {
      onDataChange({ ...data, referenceCompact: true, referenceExpandedHeight: nodeSize?.height });
      onResize?.({ width, height: REFERENCE_COMPACT_HEIGHT });
    } else {
      onDataChange({ ...data, referenceCompact: undefined, referenceExpandedHeight: undefined });
      onResize?.({ width, height: Math.max(data.referenceExpandedHeight ?? DEFAULT_OPEN_HEIGHT, MIN_OPEN_HEIGHT) });
    }
  };

  const clear = () => {
    if (isViewOnly) return;
    // An empty node shows its drop area again, so it opens up
    if (compact) onResize?.({ width: nodeSize?.width ?? 320, height: Math.max(data.referenceExpandedHeight ?? DEFAULT_OPEN_HEIGHT, MIN_OPEN_HEIGHT) });
    // A name and tag the user gave stay with the node
    onDataChange({ ...data, ...CLEARED, referenceCompact: undefined, referenceExpandedHeight: undefined, title: data.referenceNamed && data.title ? data.title : 'Reference', timestamp: Date.now() });
  };

  /** Opens the picker under the given element (the header row, or the empty node's button), or closes it. */
  const togglePicker = (anchor: HTMLElement | null) => {
    if (isViewOnly || !anchor) return;
    if (pickerAnchor) { setPickerAnchor(null); return; }
    sharing?.refresh();
    setPickerAnchor(anchor);
  };

  const rename = (name: string) => onDataChange({ ...data, title: name, referenceNamed: true, timestamp: Date.now() });
  /** The name field closes; after Enter or Esc the focus goes back to the button that opened it. */
  const closeRename = (byKey: boolean) => {
    setRenaming(false);
    if (byKey) requestAnimationFrame(() => renameRef.current?.focus({ preventScroll: true }));
  };
  const retag = (tag: ReferenceTag | undefined) => onDataChange({ ...data, referenceTag: tag, timestamp: Date.now() });

  /** Point at media the workspace already stores; media that only this page holds is taken in like an upload. */
  const pick = (choice: ReferenceChoice) => {
    setPickerAnchor(null);
    if (isViewOnly) return;
    if (/^data:/i.test(choice.url)) {
      const type = /^data:([^;,]+)/i.exec(choice.url)?.[1] || '';
      void processFile(dataUrlToFile(choice.url, fileNameFor(choice, type)), !data.referenceTag && choice.tag ? { referenceTag: choice.tag } : {});
      return;
    }
    const base = {
      ...data, ...CLEARED, referenceKind: choice.kind,
      title: data.referenceNamed && data.title ? data.title : choice.label,
      ...(!data.referenceTag && choice.tag ? { referenceTag: choice.tag } : {}),
      timestamp: Date.now(),
    };
    if (choice.kind === 'image') {
      onDataChange({ ...base, imageUrl: choice.url, originalWidth: choice.width, originalHeight: choice.height });
    } else if (choice.kind === 'video') {
      onDataChange({ ...base, videoUri: choice.url, videoFileName: choice.label, videoMimeType: choice.mimeType, videoDurationSec: choice.durationSec, videoSizeBytes: choice.sizeBytes });
    } else {
      onDataChange({ ...base, audioUrl: choice.url, audioFileName: choice.label, audioMimeType: choice.mimeType, audioDurationSec: choice.durationSec, audioSizeBytes: choice.sizeBytes });
    }
  };

  const currentUrl = kind === 'image' ? data.imageUrl : kind === 'video' ? data.videoUri : kind === 'audio' ? data.audioUrl : undefined;
  // A picture picked from the workspace has a label for a title, not a file name
  const imageFile = [data.localCopyName, data.title].find(name => /\.(png|jpe?g|webp|gif)$/i.test(name || ''));
  const imageFormat = imageFile ? formatLabel('image', undefined, imageFile) : '';
  const meta = kind === 'image'
    ? [data.originalWidth && data.originalHeight ? `${Math.round(data.originalWidth)} × ${Math.round(data.originalHeight)} px` : '', imageFormat].filter(Boolean).join(' · ')
    : kind === 'audio'
      ? [formatDuration(data.audioDurationSec), data.audioSizeBytes ? formatBytes(data.audioSizeBytes) : '', formatLabel('audio', data.audioMimeType, data.audioFileName)].filter(Boolean).join(' · ')
      : kind === 'video'
        ? [formatDuration(data.videoDurationSec), data.videoSizeBytes ? formatBytes(data.videoSizeBytes) : '', formatLabel('video', data.videoMimeType, data.videoFileName)].filter(Boolean).join(' · ')
        : '';
  const fileName = kind === 'audio' ? data.audioFileName : kind === 'video' ? data.videoFileName : data.title;
  const KindIcon = kind === 'audio' ? Music : kind === 'video' ? Film : ImageIcon;
  // Everything about the file in full, for the tooltip of the details: its name (the header may cut a long one), its file, what it holds
  const details = [...new Set([data.title, fileName, kind ? KIND_LABEL[kind] : '', meta].filter(Boolean))].join(' · ');

  return (
    <div
      className="w-full h-full flex flex-col bg-white"
      onDragOver={(e) => { e.preventDefault(); if (!isViewOnly) setIsDraggingOver(true); }}
      onDragLeave={() => setIsDraggingOver(false)}
      onDrop={(e) => { e.preventDefault(); setIsDraggingOver(false); onFiles(e.dataTransfer.files); }}
    >
      <input ref={inputRef} type="file" accept={REFERENCE_ACCEPT} className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />

      {kind ? (
        <div
          className={compact ? 'h-full flex items-center px-2 overflow-hidden' : 'flex-1 flex flex-col p-3 gap-3 min-h-0'}
          data-reference-kind={kind}
          data-reference-compact={compact || undefined}
        >
          {/* The file's row: what kind it is, its tag and details, and what can be done with it. Its
              name is the node's title, in the header; the pencil puts the name field here. What the
              file is for is said on the mark (a line of it under the preview was cut off by the card).
              The row is 50px high whatever it shows (details on one line or two, the name field, the
              small picture of a folded node), so the preview under it does not move. */}
          <div
            ref={rowRef}
            className={`ns-well rounded-xl w-full min-w-0 min-h-[50px] flex items-center gap-x-1.5 gap-y-1 p-1 ${compact ? '' : 'flex-wrap'}`}
            data-reference-file
          >
            {compact ? (
              <SmallPicture kind={kind} data={data} nodeId={nodeId} userId={userId} ownerUserId={ownerUserId} workspaceName={workspaceName} title={KIND_HINT[kind]} />
            ) : (
              <span className="ns-mark ns-mark-sm" role="img" aria-label={KIND_LABEL[kind]} title={KIND_HINT[kind]}>
                <KindIcon />
              </span>
            )}
            {renaming ? (
              <NameField value={data.title || KIND_LABEL[kind]} onCommit={rename} onClose={closeRename} />
            ) : (
              <>
                {/* Its tag, then what it holds: on one line where there is room, else the details under
                    the tag. (Open, the buttons go under them on a narrow node; folded, the node is one
                    row tall, so there the tag and the details narrow instead.) */}
                <div className={`flex-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-0.5 ${compact ? 'min-w-0' : 'min-w-[104px]'}`}>
                  <TagSelect tag={data.referenceTag} editable={!isViewOnly} onChange={retag} />
                  <span className="ns-t-meta min-w-0 truncate" title={details}>{meta || KIND_LABEL[kind]}</span>
                </div>
                {!isViewOnly && (
                  <div className="ml-auto flex items-center shrink-0">
                    <button
                      ref={renameRef}
                      type="button"
                      onClick={() => setRenaming(true)}
                      className="no-drag ns-icon-btn ns-icon-btn-sm"
                      title="Rename"
                      aria-label="Rename"
                      data-reference-rename
                    >
                      <Pencil />
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePicker(rowRef.current)}
                      disabled={isBusy}
                      aria-haspopup="dialog"
                      aria-expanded={!!pickerAnchor}
                      className="no-drag ns-icon-btn ns-icon-btn-sm"
                      title="Choose another file"
                      aria-label="Choose another file"
                      data-reference-trigger
                    >
                      <ChevronDown className={`transition-transform ${pickerAnchor ? 'rotate-180' : ''}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCompact(!compact)}
                      // The icon and the words say what a press does, so it has no pressed look: it opens and shuts the preview
                      className="no-drag ns-icon-btn ns-icon-btn-sm"
                      title={compact ? 'Show preview' : 'Hide preview'}
                      aria-label={compact ? 'Show preview' : 'Hide preview'}
                      aria-expanded={!compact}
                      data-preview-toggle
                    >
                      {compact ? <Eye /> : <EyeOff />}
                    </button>
                    {/* Not the x of the header, which deletes the node: this one is in the file's row and takes the file out */}
                    <button
                      type="button"
                      onClick={clear}
                      className="no-drag ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                      title={`Remove the ${KIND_NOUN[kind]} from this node`}
                      aria-label={`Remove the ${KIND_NOUN[kind]} from this node`}
                      disabled={isBusy}
                      data-reference-remove
                    >
                      <X />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {!compact && (
            <>
              {kind === 'audio' && <AudioView data={data} onDataChange={onDataChange} userId={userId} workspaceName={workspaceName} ownerUserId={ownerUserId} />}
              {kind === 'video' && <VideoView data={data} onDataChange={onDataChange} userId={userId} workspaceName={workspaceName} ownerUserId={ownerUserId} />}
              {kind === 'image' && <ImageView data={data} userId={userId} workspaceName={workspaceName} />}
            </>
          )}
        </div>
      ) : (
        // The empty state of every node: a round mark, a title, a line, one button. The whole
        // body takes a dropped file, so it is the light well of the Image and Video nodes' stage.
        // (16px above and below, not the pattern's 24: a new node is 300px high and this fills it.)
        <div
          className={`no-drag ns-empty py-4 flex-1 transition-colors ${isDraggingOver ? 'bg-lavender-soft' : 'bg-cream-50'}`}
          data-reference-empty
        >
          <span className="ns-mark ns-mark-lg" aria-hidden="true">{isBusy ? <Loader2 className="animate-spin" /> : <Upload />}</span>
          <span className="ns-empty-title">{isBusy ? 'Reading file' : 'Add a reference'}</span>
          <span className="ns-empty-text">An image, an audio file or a video from {sharing ? 'this project' : 'this workspace'} or your computer.</span>
          {!isBusy && !isViewOnly && (
            <>
              <button
                type="button"
                onClick={(e) => togglePicker(e.currentTarget)}
                aria-haspopup="dialog"
                aria-expanded={!!pickerAnchor}
                className="ns-btn ns-btn-primary ns-btn-xs mt-2"
                data-reference-trigger
              >
                <LayoutGrid size={14} /> {sharing ? 'Choose from project' : 'Choose from workspace'} <ChevronDown size={14} className={`transition-transform ${pickerAnchor ? 'rotate-180' : ''}`} />
              </button>
              {/* The second way in is a link, not a second button; the formats it takes are on it */}
              <span className="ns-t-small">
                or <button type="button" onClick={() => inputRef.current?.click()} className="no-drag ns-link" title="PNG, JPG, WebP, GIF · MP3, WAV · MP4, MOV, WebM" data-reference-upload>upload a file</button>, or drop one here
              </span>
            </>
          )}
        </div>
      )}

      {pickerAnchor && (
        <ReferencePicker
          anchor={pickerAnchor}
          choices={choices}
          currentUrl={currentUrl}
          userId={userId}
          ownerUserId={ownerUserId}
          workspaceName={workspaceName}
          sharedFrom={sharing ? { projectName: sharing.projectName, loading: sharing.loading } : undefined}
          onPick={pick}
          onUpload={() => { setPickerAnchor(null); inputRef.current?.click(); }}
          onClose={() => setPickerAnchor(null)}
        />
      )}

      {error && (
        // (Under the empty state, which fills the body edge to edge, it keeps its distance at the top too)
        <div className={`ns-note mx-3 mb-3 ${kind ? '' : 'mt-3'}`} data-tone="danger">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ReferenceNode;
